import React, { useState } from "react";
import { Network, Zap, Unplug, Layers, Activity, ArrowLeft, Share2, Fingerprint, ArrowUpDown, TableOfContents, Logs, Terminal, Cpu } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PoeInfo from "./PoeInfo";
import DiscoveredInterfaces from "./DiscoveredInterfaces";
import MacTableInfo from "./MacTableInfo";
import DiscoveredNeighbors from "./DiscoveredNeighbors";
import VlansInfo from "./VlansInfo";
import SwitchPorts from "./SwitchPorts";
import SwitchOverview from "./SwitchOverview";
import ServerProtocols from "./ServerProtocols";
import NetworkConnections from "./NetworkConnections";
import NetworkStats from "./Network";
import HardwareInfoTab from "./HardwareInfoTab";
import SwitchLogs from "./SwitchLogs";
import SwitchSSH from "./SwitchSSH";


const CompactSwitchDashboard: React.FC = () => {
    const navigate = useNavigate();

    // Initialize useSearchParams to sync state with URL
    const [searchParams, setSearchParams] = useSearchParams();

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;
    const hostname = info.host_name || "Switch Asset";

    const [fingerprintMode, setFingerprintMode] = useState(false);

    // Get the active tab from the URL parameter or default to "overview"
    const activeTab = searchParams.get("tab") || "overview";





    // Define the tabs array for easy mapping and rendering
    const tabs = [
        { id: "overview", label: "Overview", icon: TableOfContents },
        { id: "ssh", label: "SSH", icon: Terminal },
        { id: "poe", label: "PoE Details", icon: Zap },
        { id: "interfaces", label: "Interfaces", icon: Unplug },
        { id: "mac", label: "MAC Entries", icon: Fingerprint },
        { id: "neighbors", label: "Neighbors", icon: Share2 },
        { id: "vlans", label: "VLANs", icon: Layers },
        { id: "ports", label: "Open Ports", icon: Activity },
        { id: "connections", label: "Network Connections", icon: ArrowUpDown },
        { id: "network", label: "Network", icon: Network },
        { id: "hardware", label: "Hardware Info", icon: Cpu },
        { id: "logs", label: "Logs", icon: Logs },
    ];

    // Update the URL parameter when a tab is clicked
    const handleTabChange = (tabId: string) => {
        setSearchParams({ tab: tabId }, { replace: true });
    };

    // Function to render the correct component based on active tab
    const renderActiveTabContent = () => {
        switch (activeTab) {
            case "overview":
                return <SwitchOverview client_id={client_id} asset_id={asset_id} onDataLoaded={(hasData) => setFingerprintMode(hasData)} />;
            case "ssh":
                return <SwitchSSH client_id={client_id} asset_id={asset_id} />;
            case "poe":
                return <PoeInfo client_id={client_id} hideHeader={true} defaultItemsPerPage={24} viewType="table" />;
            case "interfaces":
                return <DiscoveredInterfaces hideHeader={true} defaultItemsPerPage={30} compact={true} />;
            case "mac":
                return <MacTableInfo hideHeader={true} defaultItemsPerPage={25} />;
            case "neighbors":
                return <DiscoveredNeighbors hideHeader={true} defaultItemsPerPage={25} compact={true} />;
            case "vlans":
                return <VlansInfo hideHeader={true} defaultItemsPerPage={25} />;
            case "ports":
                return <SwitchPorts hideHeader={true} viewType="table" />;
            case "connections":
                return <NetworkConnections client_id={client_id} asset_id={asset_id} />;
            case "network":
                return <NetworkStats client_id={client_id} asset_id={asset_id} />;
            case "hardware":
                return <HardwareInfoTab client_id={client_id} asset_id={asset_id} hideHeader={true} defaultItemsPerPage={10} />;
            case "logs":
                return <SwitchLogs asset_id={asset_id} />;
            default:
                return <SwitchOverview client_id={client_id} asset_id={asset_id} onDataLoaded={(hasData) => setFingerprintMode(hasData)} />;
        }
    };

    return (
        <div className={`flex flex-col gap-4 p-4 bg-slate-50 dark:bg-gray-900 text-slate-800 dark:text-slate-200 transition-all duration-500 ${fingerprintMode ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
            {/* Header Area */}
            <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap border-b border-slate-200 dark:border-slate-800 pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-600 rounded-lg shadow-lg shadow-orange-600/20">
                        <Network className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight leading-none mb-1">Switch Dashboard</h1>
                        <p className="text-[12px] text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Hostname : {hostname}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900/50 p-1 rounded-lg border border-slate-200 dark:border-slate-800/60 mr-2">
                        <div className={`p-1.5 rounded-md transition-colors ${fingerprintMode ? 'bg-orange-600 text-white' : 'text-slate-500 dark:text-slate-400'}`}>
                            <Fingerprint className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-[10px] font-black uppercase text-slate-600 dark:text-slate-400 tracking-wider">Fingerprint</span>
                        <button
                            onClick={() => setFingerprintMode(!fingerprintMode)}
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 focus:outline-none cursor-pointer ${fingerprintMode ? 'bg-orange-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${fingerprintMode ? 'translate-x-5.5' : 'translate-x-1'}`} />
                        </button>
                    </div>

                    <button
                        onClick={() => navigate(info.path)}
                        className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-500 dark:bg-orange-700 dark:hover:bg-orange-600 rounded-lg text-xs font-bold text-white transition-all active:scale-95 shadow-sm uppercase tracking-wide cursor-pointer"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {fingerprintMode ? (
                <div className="flex-1 min-h-0 pb-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <ServerProtocols hideHeader={false} />
                </div>
            ) : (
                <div className="flex flex-col flex-1 h-full">
                    {/* Modern Tabs Navigation */}
                    <div className="flex overflow-x-auto scrollbar-hide border-b border-slate-200 dark:border-slate-800 pb-0">
                        <div className="flex">
                            {tabs.map((tab) => {
                                const isActive = activeTab === tab.id;
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabChange(tab.id)}
                                        className={`flex items-center gap-2 px-3 py-2.5 text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap rounded-t-lg cursor-pointer
                                            ${isActive
                                                ? 'text-orange-600 dark:text-orange-500 bg-white dark:bg-white/5 backdrop-blur-lg border-x border-t border-slate-200 dark:border-white/10 shadow-sm'
                                                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/30 border-b-1 hover:border-slate-300 dark:hover:border-slate-700'
                                            }`}
                                    >
                                        <Icon className={`w-3 h-3 ${isActive ? 'text-orange-600 dark:text-orange-500' : 'text-slate-400 dark:text-slate-500'}`} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tab Content Area */}
                    <div className="flex-1 bg-white dark:bg-white/5 backdrop-blur-lg rounded-b-xl rounded-tr-xl border-x border-b border-slate-200 dark:border-white/10 shadow-lg overflow-hidden relative min-h-[500px] mb-18">
                        <div className="absolute inset-0 overflow-y-auto custom-scroll p-4 animate-in fade-in zoom-in-95 duration-200">
                            {renderActiveTabContent()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompactSwitchDashboard;
